// TAGE MAGE API Integration
import {
  API_BASE_URL,
  getAccountScopedLocalStorageKey,
  getAuthHeaders,
  isTokenValid,
} from '../../../utils/utils';
import { getStoredUser } from '../../../utils/authSession';

// Helper to get user email from session
const getUserEmail = () => {
  const user = getStoredUser() || {};
  return user.email;
};

const getScopedLocalStorageItem = (key) => {
  const scopedKey = getAccountScopedLocalStorageKey(key, getUserEmail());
  return scopedKey ? localStorage.getItem(scopedKey) : null;
};

// Helper to check if user is admin
const isAdmin = () => {
  try {
    const user = getStoredUser() || {};
    return user?.role?.name === 'admin';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

// ========================================
// ENROLLMENT API
// ========================================

export const tageApi = {
  // Enroll user in TAGE MAGE program
  enrollUser: async (enrollmentData) => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tage/enroll`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_email: getUserEmail(),
          ...enrollmentData,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to enroll user:', response.status);
        const error = await response.json().catch(() => ({}));

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else if (response.status === 422) {
          throw new Error('VALIDATION_ERROR');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error enrolling user:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR' ||
        error.message === 'VALIDATION_ERROR'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // Get enrollment details
  getEnrollment: async () => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tage/enrollment`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to fetch enrollment:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error fetching enrollment:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // Update preferred time
  updatePreferredTime: async (preferredTime, timezone) => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/tage/enrollment/update-time`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            user_email: getUserEmail(),
            preferred_time: preferredTime,
            timezone: timezone,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to update preferred time:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error updating preferred time:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // ========================================
  // LESSON API
  // ========================================

  // Get daily lessons (no cache, always fetch fresh from backend)
  // If getAllLessons=true and user is admin, fetches all lessons instead of just daily ones
  getDailyLessons: async (getAllLessons = false) => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      // Always fetch fresh from backend (no cache)
      // Check if user is admin
      const user = getStoredUser() || {};
      const isAdmin = user?.role?.name === 'admin';

      // Build URL with optional get_all parameter for admins
      let url = `${API_BASE_URL}/api/tage/daily-lessons`;
      if (getAllLessons && isAdmin) {
        url += '?get_all=true';
        console.log('🔑 Admin access: Fetching ALL lessons');
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        console.log(
          getAllLessons && isAdmin
            ? '✅ All lessons fetched (admin mode)'
            : '✅ Daily lessons fetched from backend'
        );
        return data;
      } else {
        console.error('Failed to fetch daily lessons:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error fetching daily lessons:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  getProgramWindow: async () => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tage/program-window`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        return response.json();
      }
      if (response.status === 401 || response.status === 403) {
        throw new Error('SESSION_EXPIRED');
      }
      throw new Error('API_ERROR');
    } catch (error) {
      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR'
      ) {
        throw error;
      }
      throw new Error('NETWORK_ERROR');
    }
  },

  // Get specific lesson
  getLesson: async (lessonId) => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/tage/lesson/${lessonId}`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to fetch lesson:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else if (response.status === 404) {
          throw new Error('NOT_FOUND');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error fetching lesson:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR' ||
        error.message === 'NOT_FOUND'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // Get lessons with filters
  getLessons: async (filters = {}) => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const queryParams = new URLSearchParams(filters).toString();
      const url = queryParams
        ? `${API_BASE_URL}/api/tage/lessons?${queryParams}`
        : `${API_BASE_URL}/api/tage/lessons`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to fetch lessons:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error fetching lessons:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // Get previous lessons with progress (with 5-minute cache)
  getPreviousLessons: async () => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      // Check cache first (5 minutes)
      const cached = localStorage.getItem('tage_previous_lessons_cache');
      const cacheTimestamp = localStorage.getItem(
        'tage_previous_lessons_cache_timestamp'
      );

      if (cached && cacheTimestamp) {
        const age = Date.now() - parseInt(cacheTimestamp);
        const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds

        if (age < fiveMinutes) {
          console.log(
            '✅ Using cached previous lessons (age:',
            Math.floor(age / 1000),
            'seconds)'
          );
          return JSON.parse(cached);
        } else {
          console.log('⏰ Previous lessons cache expired, fetching fresh');
        }
      }

      // Fetch from backend
      const response = await fetch(`${API_BASE_URL}/api/tage/previous-lessons`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();

        // Cache the result
        localStorage.setItem(
          'tage_previous_lessons_cache',
          JSON.stringify(data)
        );
        localStorage.setItem(
          'tage_previous_lessons_cache_timestamp',
          Date.now().toString()
        );
        console.log('✅ Previous lessons cached for 5 minutes');

        return data;
      } else {
        console.error('Failed to fetch previous lessons:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error fetching previous lessons:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // Clear previous lessons cache (call this when user completes a lesson)
  clearPreviousLessonsCache: () => {
    localStorage.removeItem('tage_previous_lessons_cache');
    localStorage.removeItem('tage_previous_lessons_cache_timestamp');
    console.log('✅ Previous lessons cache cleared');
  },

  // Clear all lessons caches (call this when user completes a lesson or updates progress)
  clearAllLessonsCaches: () => {
    // Clear daily lessons cache
    localStorage.removeItem('tage_daily_lessons_cache');
    localStorage.removeItem('tage_daily_lessons_cache_timestamp');

    // Clear previous lessons cache
    localStorage.removeItem('tage_previous_lessons_cache');
    localStorage.removeItem('tage_previous_lessons_cache_timestamp');

    console.log('✅ All lessons caches cleared');
  },

  // Get all lessons (catalog)
  getAllLessons: async () => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tage/lessons`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to fetch all lessons:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error fetching all lessons:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // Get study plans
  getStudyPlans: async () => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tage/study-plans`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to fetch study plans:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error fetching study plans:', error);

      // Re-throw with appropriate error type
      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR'
      ) {
        throw error;
      }

      // Network or other errors
      throw new Error('NETWORK_ERROR');
    }
  },

  // Get specific study plan
  getStudyPlan: async (planId) => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/tage/study-plan/${planId}`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to fetch study plan:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else if (response.status === 404) {
          throw new Error('NOT_FOUND');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error fetching study plan:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR' ||
        error.message === 'NOT_FOUND'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // ========================================
  // PROGRESS API
  // ========================================

  // Save lesson progress
  saveLessonProgress: async (progressData) => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tage/lesson-progress`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_email: getUserEmail(),
          ...progressData,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to save lesson progress:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else if (response.status === 422) {
          throw new Error('VALIDATION_ERROR');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error saving lesson progress:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR' ||
        error.message === 'VALIDATION_ERROR'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // Get lesson progress
  getLessonProgress: async (lessonId) => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/tage/lesson-progress/${lessonId}`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        // Don't log 422 errors - they're normal for lessons without progress
        if (response.status !== 422) {
          console.error('Failed to fetch lesson progress:', response.status);
        }

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else if (response.status === 404 || response.status === 422) {
          throw new Error('NOT_FOUND');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      // Don't log NOT_FOUND errors - they're expected for new lessons
      if (error.message !== 'NOT_FOUND') {
        console.error('Error fetching lesson progress:', error);
      }

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR' ||
        error.message === 'NOT_FOUND'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // Get overall progress
  getOverallProgress: async () => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tage/progress`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to fetch overall progress:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error fetching overall progress:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // ========================================
  // EXERCISE API
  // ========================================

  // Save exercise attempt
  saveExerciseAttempt: async (attemptData) => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/tage/exercise-attempt`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            user_email: getUserEmail(),
            ...attemptData,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to save exercise attempt:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else if (response.status === 422) {
          throw new Error('VALIDATION_ERROR');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error saving exercise attempt:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR' ||
        error.message === 'VALIDATION_ERROR'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // ========================================
  // TEST API
  // ========================================

  // Save test attempt
  saveTestAttempt: async (testData) => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tage/test-attempt`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_email: getUserEmail(),
          ...testData,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to save test attempt:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else if (response.status === 422) {
          throw new Error('VALIDATION_ERROR');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error saving test attempt:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR' ||
        error.message === 'VALIDATION_ERROR'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // Get all test attempts
  getTestAttempts: async () => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tage/test-attempts`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to fetch test attempts:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error fetching test attempts:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // Get specific test attempt
  getTestAttempt: async (testId) => {
    if (!isTokenValid()) {
      throw new Error('AUTH_REQUIRED');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tage/test-attempt/${testId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to fetch test attempt:', response.status);

        if (response.status === 401 || response.status === 403) {
          throw new Error('SESSION_EXPIRED');
        } else if (response.status === 404) {
          throw new Error('NOT_FOUND');
        } else {
          throw new Error('API_ERROR');
        }
      }
    } catch (error) {
      console.error('Error fetching test attempt:', error);

      if (
        error.message === 'SESSION_EXPIRED' ||
        error.message === 'AUTH_REQUIRED' ||
        error.message === 'API_ERROR' ||
        error.message === 'NOT_FOUND'
      ) {
        throw error;
      }

      throw new Error('NETWORK_ERROR');
    }
  },

  // ========================================
  // UTILITY FUNCTIONS
  // ========================================

  // Check if current user is admin
  isAdmin: () => {
    return isAdmin();
  },
};

// ========================================
// HELPER FUNCTIONS
// ========================================

// Migrate localStorage data to backend (call this once during transition)
export const migrateLocalStorageToBackend = async () => {
  try {
    const userEmail = getUserEmail();
    if (!userEmail) {
      console.warn('No user email found, skipping migration');
      return;
    }

    if (!isTokenValid()) {
      console.warn('Invalid token, skipping migration');
      return;
    }

    // Migrate completed lessons
      const completedLessons = JSON.parse(
        getScopedLocalStorageItem('tage_completed_lessons') || '[]'
      );

    // Migrate lesson progress
    const localStorageKeys = Object.keys(localStorage);
    const progressKeys = localStorageKeys.filter((key) =>
      key.startsWith('tage_lesson_progress_')
    );

    for (const key of progressKeys) {
      const lessonId = key.replace('tage_lesson_progress_', '');
      const progressData = JSON.parse(localStorage.getItem(key));

      // Convert localStorage format to backend format
      await tageApi.saveLessonProgress({
        lesson_id: lessonId,
        status: progressData.status || 'not_started',
        current_exercise_index: progressData.currentExercise || 0,
        exercises_completed: progressData.completedExercises || 0,
        exercises_correct: progressData.exerciseResults
          ? progressData.exerciseResults.filter((r) => r.isCorrect).length
          : 0,
        total_exercises: progressData.totalExercises || 0,
        exercise_results: progressData.exerciseResults || [],
        total_time_seconds: 0,
      });
    }

    // Migrate test results
    const testResultsKeys = localStorageKeys.filter(
      (key) => key.includes('tage_full_test_') && key.includes('_results')
    );

    for (const key of testResultsKeys) {
      const testData = JSON.parse(localStorage.getItem(key));
      if (testData && testData.testId) {
        await tageApi.saveTestAttempt({
          test_id: testData.testId,
          test_name: testData.testId,
          test_type: 'full_test',
          start_time: testData.completionDate
            ? new Date(testData.completionDate).toISOString()
            : new Date().toISOString(),
          end_time: testData.completionDate
            ? new Date(testData.completionDate).toISOString()
            : new Date().toISOString(),
          total_score: testData.results?.finalScore || 0,
          score_percentage: ((testData.results?.finalScore || 0) / 600) * 100,
          section_scores: testData.results?.sectionScores || {},
          detailed_scores: testData.results?.subTestResults || {},
          question_results: testData.results?.questionDetails || [],
          total_questions: testData.results?.totalQuestions || 90,
          correct_answers:
            testData.results?.questionDetails?.filter((q) => q.isCorrect)
              .length || 0,
          incorrect_answers:
            testData.results?.questionDetails?.filter(
              (q) => !q.isCorrect && q.isAnswered
            ).length || 0,
          unanswered:
            testData.results?.totalQuestions -
              testData.results?.answeredQuestions || 0,
          strengths: [],
          weaknesses: [],
          recommendations: [],
        });
      }
    }

    console.log('Migration completed successfully');
  } catch (error) {
    console.error('Error during migration:', error);
  }
};

export default tageApi;
